"use strict";
/**
 * Pad 2's cues (ai/nba/N8-taps/PLAN.md §19.5, §19a.1, §19a.4): pure functions from the folded game
 * state and this pad's own shot times to the view model in `ui/nba-pad-v2-view.ts`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NBA_SHOT_UP_MS = void 0;
exports.nbaV2Shot = nbaV2Shot;
exports.nbaBallIsDreb = nbaBallIsDreb;
exports.nbaPadV2View = nbaPadV2View;
exports.nbaTipAnswerAllowed = nbaTipAnswerAllowed;
const nba_tap_state_1 = require("../../shared/nba-tap-state");
/** A shot older than this is no longer up: the next tap on its button is a new shot (§18a.1's display rule). */
exports.NBA_SHOT_UP_MS = 24000;
/** The shot is this side's, folded as current, and under 24 s old on this pad's clock. */
function shotUp(state, side, ageMs) {
    return state !== null && state.ctx.kind === "shot" && state.ctx.side === side && ageMs !== null && ageMs < exports.NBA_SHOT_UP_MS;
}
/** The kind a 2PT or 3PT tap records now, and its label: a make while its team's shot is up, else a new shot. */
function nbaV2Shot(state, side, v, shotAgeMs) {
    if (state === null || state.ctx.kind !== "shot" || !shotUp(state, side, shotAgeMs)) {
        return { kind: v === 2 ? "shot_2" : "shot_3", label: `${v}PT`, correction: false };
    }
    return { kind: v === 2 ? "made_2" : "made_3", label: `Made ${v}`, correction: state.ctx.v !== v };
}
/** Cannot collide with a real tap id (uuids), so the fold never takes it for a retry. */
const HYPOTHETICAL_ID = "\u0000hypothetical-ball";
/** Whether a Ball tapped now on `side` would count as a defensive rebound, by folding one. */
function nbaBallIsDreb(taps, side) {
    let sessionId;
    for (let i = taps.length - 1; i >= 0 && sessionId === undefined; i--)
        if (taps[i].kind === "session_start")
            sessionId = taps[i].sessionId ?? null;
    const ball = { tapId: HYPOTHETICAL_ID, kind: "ball", side, sessionId: sessionId ?? null, padVersion: 2 };
    return (0, nba_tap_state_1.nbaStateRefold)([...taps, ball]).ptype === "after_dreb";
}
/** "8:14 pm". */
function clockText(ms) {
    const d = new Date(ms);
    const h = d.getHours();
    return `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "am" : "pm"}`;
}
function score(input) {
    const { state } = input;
    if (!input.recordComplete || state === null)
        return { kind: "loading" };
    const home = (state.total + state.margin) / 2;
    const away = (state.total - state.margin) / 2;
    if (state.fromTip)
        return { kind: "actual", home, away };
    return { kind: "partial", home, away, since: input.partialSinceMs === null ? "" : clockText(input.partialSinceMs) };
}
function sideView(input, side) {
    const { state } = input;
    const age = input.shotAgeMs[side];
    const up = shotUp(state, side, age);
    const hasBall = state !== null && state.poss === side;
    const faint = !up && state !== null && state.poss !== null && state.poss !== side;
    const cue = (v) => {
        const { kind, label } = nbaV2Shot(state, side, v, age);
        return { kind, label, lit: up, faint };
    };
    const loose = state !== null && state.ctx.kind === "loose" && state.ctx.side === side;
    return {
        shot2: cue(2),
        shot3: cue(3),
        ballLabel: state !== null && nbaBallIsDreb(input.taps, side) ? "D-REB" : "Ball",
        missedLit: up,
        offRebLit: up || loose,
        hasBall,
        possUncertain: hasBall && state !== null && state.contextUncertain,
    };
}
/** The fold's latest answer pressed, else the picker's guess. */
const tipPrompt = (answer, guess) => answer === null ? { preselect: guess, answered: false } : { preselect: answer, answered: true };
function nbaPadV2View(input) {
    const { state } = input;
    return {
        away: sideView(input, "away"),
        home: sideView(input, "home"),
        score: score(input),
        // Until the record is complete the fold is only this phone's taps: its period is a guess (§19a.4, §19e).
        period: input.recordComplete ? state?.period ?? null : null,
        pulled: state?.pulled === true ? true : input.recordComplete ? false : null,
        // Re-answerable until a possession tap, after which no answer can change the opening (§19a.1, §19e).
        tipPrompt: input.recordComplete && state !== null && !state.possTapSeen ? tipPrompt(state.tipAnswer, input.espnPreselect ?? null) : null,
    };
}
/** §19a.1: a Yes after a possession tap is refused on the pad; a No never changes the opening. */
function nbaTipAnswerAllowed(state, atTip) {
    if (!atTip)
        return { ok: true };
    if (state === null)
        return { ok: false, reason: "record loading" };
    return state.possTapSeen ? { ok: false, reason: "possession already tapped" } : { ok: true };
}
